import { Injectable } from '@angular/core';

import { addDays, compareAsc, endOfDay, isBefore, startOfDay } from 'date-fns';
import { Duration } from 'luxon';
import { filter, map, mergeMap, of } from 'rxjs';

import { CamBaseService, GraphEndpoint, HandleComplexRequest, HandleSimpleRequest } from '@camelot/server';
import { getFirstMondayOfCurrentYear, getUniqueArray, isNonNullable } from '@camelot/utils';

import {
  DayAvailability,
  ModifyDayAvailabilitiesPayloadInput,
  PlanningAvailability,
  dayAvailabilityProps,
} from './dto/day-availability';
import { Function } from './dto/function';
import { UPDATE_DAY_AVAILABILITY } from './user-mutations';
import { GET_DAY_AVAILABILITIES, GET_PLANNING_AVAILABILITIES } from './user-queries';

export type UserUnavailabilities = { start: Date; end: Date; recurrenceRule: string };
const graphEndpoint: GraphEndpoint = {
  clientName: 'userService',
  endpoint: 'user',
};

@Injectable({
  providedIn: 'root',
})
export class CamAvailabilitiesService extends CamBaseService {
  readonly availabilities = new HandleSimpleRequest<DayAvailability[]>();
  readonly planningAvailabilities = new HandleComplexRequest<PlanningAvailability[]>();

  private computeUnavailabilitiesFromAvailabilities(availabilities: PlanningAvailability[]): UserUnavailabilities[] {
    const yearStartDate = getFirstMondayOfCurrentYear();

    const availabilityMap = availabilities.reduce((map, availability) => {
      const dayList = map.get(availability.dayOfWeek) || [];
      dayList.push(availability);
      map.set(availability.dayOfWeek, dayList);
      return map;
    }, new Map<number, PlanningAvailability[]>());

    return Array.from<UserUnavailabilities[]>({ length: 7 }).flatMap((_, dayOffset) => {
      const currentDate = addDays(yearStartDate, dayOffset);
      const dayStart = startOfDay(currentDate);
      const dayEnd = endOfDay(currentDate);

      const slots = (availabilityMap.get(dayOffset) || [])
        .map(av => {
          const startDuration = Duration.fromISO(av.startTime);
          const endDuration = Duration.fromISO(av.endTime);

          const slotStart = new Date(currentDate.getTime() + startDuration.as('milliseconds'));
          const slotEnd = new Date(currentDate.getTime() + endDuration.as('milliseconds'));

          return { start: slotStart, end: slotEnd };
        })
        .sort((a, b) => compareAsc(a.start, b.start));

      if (slots.length === 0) {
        return [{ start: dayStart, end: dayEnd, recurrenceRule: 'FREQ=WEEKLY' }];
      }

      return slots.reduce<UserUnavailabilities[]>((unavails, slot, index, all) => {
        const lastEnd = index === 0 ? dayStart : all[index - 1].end;

        if (isBefore(lastEnd, slot.start)) {
          unavails.push({ start: lastEnd, end: slot.start, recurrenceRule: 'FREQ=WEEKLY' });
        }

        if (index === all.length - 1 && isBefore(slot.end, dayEnd)) {
          unavails.push({ start: slot.end, end: dayEnd, recurrenceRule: 'FREQ=WEEKLY' });
        }

        return unavails;
      }, []);
    });
  }

  readonly mergeUnavailabilities = (userIdSelected: string) =>
    this.planningAvailabilities
      .get$(userIdSelected)
      .pipe(map(availabilities => this.computeUnavailabilitiesFromAvailabilities(availabilities)));

  readonly mergeUnavailabilitiesByUser = (cacheKey: string) =>
    this.planningAvailabilities.get$(cacheKey).pipe(
      map(availabilities => {
        return getUniqueArray(availabilities.map(availability => availability.userId))
          .filter(isNonNullable)
          .reduce<{ [key: string]: UserUnavailabilities[] }>((result, userId) => {
            result[userId] = this.computeUnavailabilitiesFromAvailabilities(
              availabilities.filter(availability => availability.userId === userId)
            );
            return result;
          }, {});
      })
    );

  constructor() {
    super();
    super.registerRoutes({ graphEndpoint: graphEndpoint });
  }

  public fetchUserDayAvailabilitiesForFunction$(functionId: string, userId: string | null = null) {
    return this.availabilities.fetch(
      this._graphService
        .fetchPagedQueryList<DayAvailability>(
          GET_DAY_AVAILABILITIES(
            userId
              ? `where: { userId: { eq: "${userId}" }, functionId: { eq: "${functionId}"} }`
              : `where: { userId: { eq: null }, functionId: { eq: "${functionId}"} }`,
            `
                ${dayAvailabilityProps.get('id')}
                ${dayAvailabilityProps.get('startTime')}
                ${dayAvailabilityProps.get('endTime')}
                ${dayAvailabilityProps.get('dayOfWeek')}
                ${dayAvailabilityProps.get('functionId')}
                ${dayAvailabilityProps.get('userId')}
              `
          ),
          'dayAvailabilities',
          graphEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => data.items ?? [])
        )
    );
  }

  public fetchPlanningAvailabilities$(userId: string) {
    return this.planningAvailabilities.fetch(
      userId,
      this._graphService
        .fetchPagedQueryList<PlanningAvailability>(
          GET_PLANNING_AVAILABILITIES({
            where: { userId: { eq: userId } },
            props: `
              items {
                  ${dayAvailabilityProps.get('userId')}
                  ${dayAvailabilityProps.get('functionId')}
                  ${dayAvailabilityProps.get('startTime')}
                  ${dayAvailabilityProps.get('endTime')}
                  ${dayAvailabilityProps.get('dayOfWeek')}
              }
              `,
          }),
          'planningDayAvailabilities',
          graphEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => data.items ?? [])
        )
    );
  }

  public fetchPlanningAvailabilitiesForUsers$(usersId: string[]) {
    return this.planningAvailabilities.fetch(
      usersId.join('-'),
      this._graphService
        .fetchPagedQueryList<PlanningAvailability>(
          GET_PLANNING_AVAILABILITIES({
            where: { userId: { in: usersId } },
            props: `
              items {
                  ${dayAvailabilityProps.get('userId')}
                  ${dayAvailabilityProps.get('functionId')}
                  ${dayAvailabilityProps.get('startTime')}
                  ${dayAvailabilityProps.get('endTime')}
                  ${dayAvailabilityProps.get('dayOfWeek')}
              }
              `,
          }),
          'planningDayAvailabilities',
          graphEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => data.items ?? [])
        )
    );
  }

  public updateAvailability$(availaibilitiesModifierPayload: Partial<ModifyDayAvailabilitiesPayloadInput>) {
    return this._graphService
      .mutateBuilder<Function>(UPDATE_DAY_AVAILABILITY(availaibilitiesModifierPayload), graphEndpoint.clientName, [
        'dayAvailabilities',
      ])
      .pipe(
        filter(isNonNullable),
        mergeMap(() =>
          availaibilitiesModifierPayload?.functionId
            ? this.fetchUserDayAvailabilitiesForFunction$(
                availaibilitiesModifierPayload?.functionId,
                availaibilitiesModifierPayload.userId
              )
            : of(null)
        )
      );
  }
}
